import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CustomPayment } from '@apply/api/src/api/PaymentApi';

const CUSTOM_PAYMENT_KEY = (paymentGuid: string) => ['customPayment', paymentGuid];

// Safe default object so no undefined fields break your UI
const createDefaultPayment = (paymentGuid: string): CustomPayment => ({
    paymentGuid,
    billingDetails: {
        address: {
            city: '',
            country: '',
            line1: '',
            line2: '',
            postal_code: '',
            state: '',
        },
        email: '',
        name: '',
        phone: '',
    },
    isPropertyInArizona: false,
    paymentPurpose: '',
    propertyLoanReference: '',
    amount: undefined,
    cardNumber: '',
    cardFocus: undefined,
    cvc: '',
    cardType: undefined,
    expirationDate: '',
});

export const useCustomPayment = (paymentGuid: string) => {
    const queryClient = useQueryClient();

    // Load or initialize the full object
    const { data: payment } = useQuery({
        queryKey: CUSTOM_PAYMENT_KEY(paymentGuid),
        queryFn: () => createDefaultPayment(paymentGuid),
        staleTime: Infinity,
        gcTime: Infinity,
    });

    // Setter replaces the entire object (used by mutation hook)
    const setPayment = (updater: (prev: CustomPayment) => CustomPayment) => {
        queryClient.setQueryData(CUSTOM_PAYMENT_KEY(paymentGuid), (prev: CustomPayment | undefined) => {
            const base = prev ?? createDefaultPayment(paymentGuid);
            return updater(base);
        });
    };

    return [payment!, setPayment] as const;
};
